import axios from "axios";
import { config_UPTIME } from "../../Constants";
import { getItem } from "../../utils/DataProtection";

const unwrapCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.result)) {
    return payload.result;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  return [];
};

const getAuthorizationHeader = () => {
  try {
    const userToken = getItem("user");
    return userToken ? { Authorization: `Bearer ${JSON.parse(userToken)}` } : {};
  } catch (error) {
    return {};
  }
};

const getStoredLegalEntityNodeId = () => {
  try {
    const storedValue = getItem("legalentitynodeid");
    return storedValue ? JSON.parse(storedValue) : "";
  } catch (error) {
    return "";
  }
};

const normalizeAttachments = (equipmentType) =>
  (equipmentType?.hasattachment ?? [])
    .map((entry, index) => {
      const attachment = entry?.attachment ?? entry;
      const localizedFiles =
        attachment?.file && typeof attachment.file === "object" ? attachment.file : {};

      if (!attachment || !Object.keys(localizedFiles).length) {
        return null;
      }

      return {
        id: attachment?.nodeid ?? `${equipmentType?.nodeid || "equipment-type"}-attachment-${index}`,
        title: attachment?.title ?? "",
        description: attachment?.description ?? "",
        localizedFiles,
        raw: attachment,
      };
    })
    .filter(Boolean);

const getEquipmentClassNames = (equipmentType) => {
  const names = (equipmentType?.hasequipmentclass ?? [])
    .map((entry) => entry?.equipmentclass?.name ?? entry?.name ?? "")
    .filter(Boolean);

  if (names.length > 0) {
    return names.join(", ");
  }

  return (
    equipmentType?.equipmentclass?.name ??
    equipmentType?.productclass?.name ??
    equipmentType?.productclass ??
    equipmentType?.productClass ??
    ""
  );
};

const createRow = (equipmentType, brandName, index) => ({
  id:
    equipmentType?.nodeid ??
    equipmentType?.nodeId ??
    equipmentType?.id ??
    `${brandName || "brand"}-${equipmentType?.model || index}`,
  type:
    equipmentType?.model ??
    equipmentType?.name ??
    equipmentType?.title ??
    equipmentType?.equipmenttypename ??
    "",
  brand:
    brandName ??
    equipmentType?.brand?.name ??
    equipmentType?.brandname ??
    equipmentType?.brandName ??
    "",
  equipmentClass: getEquipmentClassNames(equipmentType),
  productClass: getEquipmentClassNames(equipmentType),
  machineClass:
    equipmentType?.machineclass?.name ??
    equipmentType?.machineclass ??
    equipmentType?.machineClass ??
    "",
  imageUrl:
    equipmentType?.image?.url ??
    equipmentType?.imageurl ??
    equipmentType?.imageUrl ??
    "",
  attachments: normalizeAttachments(equipmentType),
  raw: equipmentType,
});

const normalizeEquipmentTypes = (payload) => {
  const collection = unwrapCollection(payload);

  return collection.flatMap((entry, entryIndex) => {
    if (Array.isArray(entry?.data)) {
      return entry.data.flatMap((dataEntry, dataIndex) => {
        if (dataEntry?.legalentity?.hasbrand) {
          return dataEntry.legalentity.hasbrand.flatMap((brandEntry, brandIndex) => {
            const brand = brandEntry?.brand;
            const brandName = brand?.name ?? "";

            return (brand?.hasequipmenttype ?? []).map((typeEntry, typeIndex) =>
              createRow(
                typeEntry?.equipmenttype ?? typeEntry,
                brandName,
                `${entryIndex}-${dataIndex}-${brandIndex}-${typeIndex}`
              )
            );
          });
        }

        if (dataEntry?.equipmenttype) {
          return [
            createRow(
              dataEntry.equipmenttype,
              dataEntry?.brand?.name ?? dataEntry?.equipmenttype?.brand?.name ?? "",
              `${entryIndex}-${dataIndex}`
            ),
          ];
        }

        return [createRow(dataEntry, dataEntry?.brand?.name ?? "", `${entryIndex}-${dataIndex}`)];
      });
    }

    if (entry?.brand?.hasequipmenttype) {
      return entry.brand.hasequipmenttype.map((typeEntry, typeIndex) =>
        createRow(typeEntry?.equipmenttype ?? typeEntry, entry.brand?.name, `${entryIndex}-${typeIndex}`)
      );
    }

    if (entry?.hasequipmenttype) {
      const brandName = entry?.name ?? entry?.brand?.name ?? "";
      return entry.hasequipmenttype.map((typeEntry, typeIndex) =>
        createRow(typeEntry?.equipmenttype ?? typeEntry, brandName, `${entryIndex}-${typeIndex}`)
      );
    }

    if (entry?.equipmenttype) {
      return [createRow(entry.equipmenttype, entry?.brand?.name, entryIndex)];
    }

    return [createRow(entry, entry?.brand?.name, entryIndex)];
  }).filter((row) => row.type || row.brand || row.equipmentClass || row.productClass || row.machineClass);
};

export const fetchProductTypes = async (language = "de") => {
  const legalentitynodeid = getStoredLegalEntityNodeId();

  const response = await axios({
    method: "get",
    url: `${config_UPTIME.url.API_URL}/fette/equipmenttypes`,
    params: {
      lang: language || "de",
      ...(legalentitynodeid ? { legalentitynodeid } : {}),
    },
    headers: {
      "Content-Type": "application/json",
      ...getAuthorizationHeader(),
    },
  });

  return normalizeEquipmentTypes(response.data);
};

const normalizeEquipmentTypeDetail = (payload, nodeId) => {
  const rows = normalizeEquipmentTypes(payload);
  const matchingRow = rows.find((row) => row.id === nodeId || row.nodeid === nodeId);

  if (!matchingRow) {
    return null;
  }

  return {
    ...matchingRow,
    attachments: normalizeAttachments(matchingRow.raw),
  };
};

export const fetchProductTypeByNodeId = async (nodeId, language = "de") => {
  const response = await axios({
    method: "post",
    url: `${config_UPTIME.url.API_URL}/equipmenttype`,
    data: {
      equipmenttypenodeid: nodeId,
      lang: language || "de",
    },
    headers: {
      "Content-Type": "application/json",
      ...getAuthorizationHeader(),
    },
  });

  return normalizeEquipmentTypeDetail(response.data, nodeId);
};
